import { RiCloseLine, RiFilterLine } from "@remixicon/react";
import React, {
    HTMLAttributes, ReactElement,
    useEffect,
    useState,
} from "react";
import useRouting, { getSearchParamsWithObjects } from "../../../../contexts/Routing";
import Button from "../../button/Button";
import Dropdown from "../../dropdown/Dropdown";
import useTableData, { Events } from "../base";
import Styles from "../Table.module.scss";

export interface TableControlsFilterProps extends HTMLAttributes<HTMLButtonElement> {
    shouldWriteUrl?: boolean;
}

const TableControlsFilters = React.forwardRef((
    args: TableControlsFilterProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
): ReactElement => {
    const {
        className,
        shouldWriteUrl,
    } = args;

    const [shouldShow, setShouldShow] = useState(false);

    const data = useTableData();
    const countFilters = data.filters.count(row => row.isActive === true);

    const updatedDataGeneration = data.events.useEvent(Events.UPDATED_DATA);

    const updatedFilterGeneration = data.events.useEvent(Events.UPDATED_FILTER);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { getSearchParams, historyPushSearch } = useRouting();

    useEffect(() => {
        if (!shouldWriteUrl) {
            return;
        }
        const search = getSearchParamsWithObjects() as Record<string, string>;
        data.filters.forEach(filter => {
            if (filter.deserialize && filter.filterKey) {
                const serialized = search[filter.filterKey];
                const value = serialized ? filter.deserialize(serialized) : undefined;
                if (value) {
                    filter.setValue(value);
                }
            }
        });
    }, [updatedDataGeneration]);

    useEffect(() => {
        if (!shouldWriteUrl) {
            return;
        }
        const search = getSearchParams() as Record<string, string | string[] | Record<string, string>>;
        data.filters.forEach(filter => {
            if (filter.serialize && filter.filterKey) {
                const serialized = filter.serialize(filter.value);
                if (typeof serialized === "object" && !Array.isArray(serialized)) {
                    Object.entries(serialized).forEach(([key, value]) => {
                        if (value) {
                            search[`${filter.filterKey}.${key}`] = value;
                        } else {
                            delete search[`${filter.filterKey}.${key}`];
                        }
                    });
                } else if (serialized) {
                    search[filter.filterKey] = serialized;
                } else {
                    delete search[filter.filterKey];
                }
            }
        });
        historyPushSearch(search);
    }, [updatedFilterGeneration]);

    return (
        <Dropdown
            ref={ref}
            autoClose="outside"
            show={shouldShow}
            className={className}
            onToggle={() => {
                setShouldShow(!shouldShow);
                // Set Show
            }}
        >
            <Dropdown.Toggle
                variant="filter"
                prefixIcon={<RiFilterLine />}
                onClick={() => setShouldShow(!shouldShow)}
                isActive={countFilters > 0}
            >
                Filters
                {countFilters > 0 && `: ${countFilters} selected`}
            </Dropdown.Toggle>
            <Dropdown.Menu className={Styles["panel-filters"]}>
                <div className={Styles["panel-filters-list"]}>
                    {data.filters.map(filter => (
                        filter.render && (
                            <div className={Styles["panel-filter-row"]}>
                                <div className={Styles["panel-filter-control"]}>
                                    {filter.render?.(filter.value, filter.setValue)}
                                </div>
                                <Button
                                    prefixIcon={<RiCloseLine />}
                                    size="small"
                                    variant="none"
                                    onClick={() => {
                                        filter.onReset();
                                    }}
                                    className={filter.isActive ? "visible" : "invisible"}
                                />
                            </div>
                        )
                    ))}
                </div>

                <div>
                    <Button
                        onClick={() => {
                            data.resetFilters();
                            setShouldShow(false);
                        }}
                        variant="ghost-blue"
                        size="small"
                    >
                        Clear All Filters
                    </Button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
});
TableControlsFilters.defaultProps = {
    shouldWriteUrl: false,
};

export default TableControlsFilters;
